import { z } from 'zod';

const NotificationCommonSchema = z.object({
    id: z.string(),
    universeId: z.string(),
    status: z.union([z.literal('pending'), z.literal('ongoing')]),
    accountId: z.number(),
    channels: z.array(z.string()),
    createdAt: z.string(),
});

const NotificationChatRequestDetailsSchema = z.object({
    assignUrl: z.string(),
    chatId: z.string(),
    message: z
        .object({
            id: z.string(),
            tags: z.array(z.string()),
            type: z.string(),
            sender: z.object({
                id: z.number(),
                name: z.string(),
                type: z.string(),
            }),
        })
        .optional()
        .nullable(),
    content: z.object({ text: z.string() }).optional().nullable(),
    participants: z.array(
        z.object({
            account: z.object({
                id: z.number(),
                type: z.string(),
                name: z.string(),
            }),
            role: z.string(),
        })
    ),
});

const NotificationBetReferralDetailsSchema = z.object({
    stakeFactor: z.number(),
    totalStake: z.number(),
    currency: z.string(),
    betTypes: z.array(z.string()),
    sports: z.array(
        z.object({
            id: z.string(),
            name: z.string(),
            url: z.string(),
        })
    ),
    assignee: z
        .object({
            id: z.number(),
            type: z.string(),
            name: z.string(),
        })
        .nullable(),
    freebet: z.boolean(),
});

const NotificationChatRequestSchema = NotificationCommonSchema.extend({
    type: z.literal('chat-request'),
    status: z.union([z.literal('pending'), z.literal('ongoing')]),
    details: NotificationChatRequestDetailsSchema,
});

const NotificationBetReferralSchema = NotificationCommonSchema.extend({
    type: z.literal('bet-referral'),
    accountName: z.string(),
    expiresAt: z.string().optional().nullable(),
    details: NotificationBetReferralDetailsSchema,
});

const NotificationGamstopPositiveDetailsSchema = z.object({
    updatedAt: z.string().optional(),
    underExclusion: z.boolean(),
    previouslyPositive: z.boolean(),
});

const NotificationGamstopPositiveSchema = NotificationCommonSchema.extend({
    type: z.literal('gamstop-positive'),
    status: z.union([z.literal('pending'), z.literal('ongoing'), z.literal('assigned')]),
    accountName: z.string().optional(),
    assignee: z
        .object({
            id: z.number(),
            type: z.string(),
            name: z.string(),
        })
        .nullable()
        .optional(),
    details: NotificationGamstopPositiveDetailsSchema,
});

const NotificationGamstopNegativeSchema = NotificationCommonSchema.extend({
    type: z.literal('gamstop-negative'),
    status: z.union([z.literal('pending'), z.literal('ongoing'), z.literal('assigned')]),
    accountName: z.string().optional(),
    assignee: z
        .object({
            id: z.number(),
            type: z.string(),
            name: z.string(),
        })
        .nullable()
        .optional(),
    details: NotificationGamstopPositiveDetailsSchema,
});

const NotificationSelfExclusionItemBaseSchema = NotificationCommonSchema.extend({
    status: z.union([z.literal('pending'), z.literal('ongoing'), z.literal('assigned')]),
    accountName: z.string().optional(),
    assignee: z
        .object({
            id: z.number(),
            type: z.string(),
            name: z.string(),
        })
        .nullable()
        .optional(),
});

const NotificationMohPopupTriggeredDetailsSchema = z
    .object({
        source: z.string().optional(),
        triggerId: z.union([z.string(), z.number()]).optional(),
        triggerName: z.string().optional(),
        popupId: z.union([z.string(), z.number()]).optional(),
        automationName: z.string().optional(),
        triggeredAt: z.string().optional(),
        riskLevel: z.string().optional(),
    })
    .strict();

const NotificationMohPopupTriggeredSchema = NotificationCommonSchema.extend({
    type: z.literal('moh-popup-triggered'),
    status: z.union([z.literal('pending'), z.literal('ongoing'), z.literal('assigned')]),
    accountName: z.string().optional(),
    assignee: z
        .object({
            id: z.number(),
            type: z.string(),
            name: z.string(),
        })
        .nullable()
        .optional(),
    details: NotificationMohPopupTriggeredDetailsSchema.optional().default({}),
});

const NotificationSelfExclusionRemovedDetailsSchema = z.object({
    removedAt: z.string(),
    startsAt: z.string().optional(),
});

const NotificationSelfExclusionRemovedSchema = NotificationSelfExclusionItemBaseSchema.extend({
    type: z.literal('self-exclusion-removed'),
    details: NotificationSelfExclusionRemovedDetailsSchema,
});

const NotificationSelfExclusionAppliedDetailsSchema = z.object({
    startsAt: z.string(),
    reactivationAllowedAfter: z.string(),
});

const NotificationSelfExclusionAppliedSchema = NotificationSelfExclusionItemBaseSchema.extend({
    type: z.literal('self-exclusion-applied'),
    details: NotificationSelfExclusionAppliedDetailsSchema,
});

export const SelfExclusionNotificationEntitySchema = z.enum(['self-exclusion-applied', 'self-exclusion-removed']);

export const NotificationTypeFieldSchema = z.object({
    type: z.string(),
});

export const NotificationMainChannelItemSchema = z.discriminatedUnion('type', [
    NotificationChatRequestSchema,
    NotificationBetReferralSchema,
    NotificationGamstopPositiveSchema,
]);

export const NotificationGamstopChannelItemSchema = z.discriminatedUnion('type', [
    NotificationGamstopPositiveSchema,
    NotificationGamstopNegativeSchema,
]);

export const NotificationSelfExclusionChannelItemSchema = z.discriminatedUnion('type', [
    NotificationSelfExclusionAppliedSchema,
    NotificationSelfExclusionRemovedSchema,
]);

export const NotificationMohPopupTriggersChannelItemSchema = z.discriminatedUnion('type', [
    NotificationMohPopupTriggeredSchema,
]);

export const NotificationMainChannelSchema = z.array(NotificationMainChannelItemSchema);
export const NotificationGamstopChannelSchema = z.array(NotificationGamstopChannelItemSchema);
export const NotificationSelfExclusionChannelSchema = z.array(NotificationSelfExclusionChannelItemSchema);
export const NotificationMohPopupTriggersChannelSchema = z.array(NotificationMohPopupTriggersChannelItemSchema);

export type NotificationMainChannelType = z.infer<typeof NotificationMainChannelSchema>;
export type NotificationGamstopChannelType = z.infer<typeof NotificationGamstopChannelSchema>;
export type NotificationSelfExclusionChannelType = z.infer<typeof NotificationSelfExclusionChannelSchema>;
export type NotificationMohPopupTriggersChannelType = z.infer<typeof NotificationMohPopupTriggersChannelSchema>;
export type BetReferralNotificationType = z.infer<typeof NotificationBetReferralSchema>;
export type ChatRequestNotificationType = z.infer<typeof NotificationChatRequestSchema>;
export type GamstopPositiveNotificationType = z.infer<typeof NotificationGamstopPositiveSchema>;
export type GamstopNegativeNotificationType = z.infer<typeof NotificationGamstopNegativeSchema>;
export type GamstopNotificationType = GamstopPositiveNotificationType | GamstopNegativeNotificationType;
export type SelfExclusionAppliedNotificationType = z.infer<typeof NotificationSelfExclusionAppliedSchema>;
export type SelfExclusionRemovedNotificationType = z.infer<typeof NotificationSelfExclusionRemovedSchema>;
export type SelfExclusionNotificationType = SelfExclusionAppliedNotificationType | SelfExclusionRemovedNotificationType;
export type MohPopupTriggeredNotificationType = z.infer<typeof NotificationMohPopupTriggeredSchema>;

export const ChatResponseSchema = z.object({
    id: z.string(),
    content: z.object({ text: z.string() }).optional(),
    participants: z
        .array(
            z.object({
                account: z.object({
                    id: z.number(),
                    type: z.string(),
                    name: z.string(),
                }),
                role: z.string(),
            })
        )
        .optional()
        .nullable(),
});

export type ChatResponseSchemaType = z.infer<typeof ChatResponseSchema>;
